<template>
  <div class="shm-description-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>x = A cos(ωt + φ)$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 混合渲染区 -->
      <div class="canvas-mixed">
        <svg width="100%" height="240" viewBox="0 0 500 240">
           <!-- 左侧：旋转矢量圆 -->
           <g transform="translate(100, 120)">
              <circle r="60" fill="none" stroke="rgba(255,255,255,0.1)" stroke-dasharray="4,4" />
              <!-- X轴 Y轴 -->
              <line x1="-70" y1="0" x2="70" y2="0" stroke="rgba(255,255,255,0.05)" />
              <line x1="0" y1="-70" x2="0" y2="70" stroke="rgba(255,255,255,0.05)" />
              
              <!-- 旋转矢量 -->
              <line x1="0" y1="0" :x2="vectorPos.x" :y2="vectorPos.y" stroke="#ff4757" stroke-width="3" marker-end="url(#arr-v)" />
              <text :x="vectorPos.x/2" :y="vectorPos.y/2 - 10" fill="#ff4757" font-size="10">A</text>
              
              <!-- 投影垂线 -->
              <line :x1="vectorPos.x" :y1="vectorPos.y" :x2="vectorPos.x" y2="0" stroke="#00d2ff" stroke-width="1" stroke-dasharray="2,2" />
              <circle :cx="vectorPos.x" cy="0" r="5" fill="#00d2ff" shadow="0 0 10px #00d2ff" />
           </g>

           <!-- 中间连接线 -->
           <line :x1="100 + vectorPos.x" y1="120" :x2="220" :y2="120 + vectorPos.y" stroke="rgba(0, 210, 255, 0.2)" stroke-dasharray="3,3" />

           <!-- 右侧：波形图 -->
           <g transform="translate(220, 120)">
              <line x1="0" y1="0" x2="250" y2="0" stroke="rgba(255,255,255,0.2)" marker-end="url(#arr-axis)" />
              <path :d="sinePath" fill="none" stroke="#2ed573" stroke-width="2" />
              <circle cx="0" :cy="vectorPos.y" r="4" fill="#ff4757" />
           </g>

           <defs>
             <marker id="arr-v" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"> <polygon points="0 0, 8 3, 0 6" fill="#ff4757" /> </marker>
             <marker id="arr-axis" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"> <polygon points="0 0, 6 2, 0 4" fill="rgba(255,255,255,0.2)" /> </marker>
           </defs>
        </svg>
      </div>

      <!-- 参数控制与解读 -->
      <div class="panels-container">
        <div class="control-card glass">
           <div class="row">
              <div class="col">
                <label>振幅 A: {{ amplitude }}</label>
                <el-slider v-model="amplitude" :min="10" :max="60" size="mini"></el-slider>
              </div>
              <div class="col">
                <label>周期 T: {{ (10/frequency).toFixed(2) }}s</label>
                <el-slider v-model="frequency" :min="0.1" :max="2" :step="0.1" size="mini"></el-slider>
              </div>
           </div>
           <div class="row">
              <div class="col">
                 <label>初相位 φ: {{ phase }}°</label>
                 <el-slider v-model="phase" :min="0" :max="360" size="mini"></el-slider>
              </div>
              <div class="col bottom-align">
                 <el-button type="primary" size="mini" @click="togglePlay">{{ playing ? '暂停' : '继续' }}</el-button>
              </div>
           </div>
        </div>

        <div class="concept-note">
           <h5>几何对应关系：</h5>
           <p>1. 矢量的<strong>长度</strong>对应简谐运动的<strong>振幅A</strong>。</p>
           <p>2. 矢量的<strong>旋转角速度</strong>对应<strong>圆频率ω</strong>。</p>
           <p>3. 矢量的<strong>竖直投影分量</strong>即为在该时刻的<strong>位移x</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SHMDescriptionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      amplitude: 50,
      frequency: 0.5,
      phase: 0,
      time: 0,
      playing: true
    }
  },
  computed: {
    vectorPos() {
      const angle = (this.time * this.frequency * 2 * Math.PI) + (this.phase * Math.PI / 180);
      return {
        x: this.amplitude * Math.cos(angle),
        y: this.amplitude * Math.sin(angle)
      };
    },
    sinePath() {
      let d = "";
      const baseAngle = (this.time * this.frequency * 2 * Math.PI) + (this.phase * Math.PI / 180);
      for(let x=0; x<250; x++) {
          const t_offset = x / 50;
          const currentAngle = baseAngle - t_offset;
          const y = this.amplitude * Math.sin(currentAngle);
          d += (x === 0 ? "M" : "L") + ` ${x} ${y}`;
      }
      return d;
    }
  },
  methods: {
    togglePlay() { this.playing = !this.playing; },
    animate() {
      if (this.playing) {
        this.time += 0.05;
      }
      this.frameReq = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.frameReq); }
}
</script>

<style scoped lang="scss">
.shm-description-viz {
  flex: 1; display: flex; flex-direction: column; gap: 15px; height: 100%; overflow: hidden;
}

.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .formula-chip { padding: 4px 12px; background: rgba(255,255,255,0.05); border-radius: 8px; color: #2ed573; font-family: serif; font-style: italic; }
}

.canvas-mixed {
  flex: 1.2; background: rgba(0,0,0,0.3); border-radius: 12px; border: 1px solid rgba(255,255,255,0.05);
}

.panels-container {
  flex: 0.8; display: flex; flex-direction: column; gap: 15px; overflow-y: auto; padding-bottom: 10px;
}

.control-card {
  background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 15px;
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 12px; &:last-child { margin-bottom: 0; } }
  .col { 
    label { display: block; font-size: 0.7rem; color: #aaa; margin-bottom: 6px; }
    &.bottom-align { display: flex; align-items: flex-end; .el-button { width: 100%; } }
  }
}

.concept-note {
  background: rgba(46, 213, 115, 0.05); border-radius: 12px; padding: 15px; border-left: 3px solid #2ed573;
  h5 { margin: 0 0 8px 0; color: #2ed573; font-size: 0.85rem; }
  p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; }
}

circle { transition: r 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
</style>
